import type { TextareaHTMLAttributes } from "react";

import { cn } from "utils/cn";

import { FormControl, FormItem, FormMessage } from "components/Form";
import Label from "components/Label";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  wrapperClassName?: string;
  isPristine?: boolean;
}

function Textarea({
  label,
  wrapperClassName = "",
  className,
  id,
  name,
  disabled,
  isPristine,
  ...props
}: TextareaProps) {
  const textareaId = id ?? name;
  const textareaClasses = cn(
    "file:text-general-gray-900 placeholder:text-general-gray-500 selection:bg-general-gray-800 selection:text-general-gray-200 border-general-gray-300 flex min-h-24 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base font-normal transition-all duration-300 outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    "focus-visible:shadow-[0_0_0_2px_theme(colors.general.gray.500),inset_0_0_0_1px_theme(colors.general.gray.400)]",
    "focus-visible:border-1 focus-visible:border-transparent",
    "aria-invalid:ring-general-red-800/20 dark:aria-invalid:ring-general-red-800/40 aria-invalid:border-general-red-800",
    className,
  );

  const textareaElement = (
    <textarea id={textareaId} name={name} disabled={disabled} className={textareaClasses} {...props} />
  );

  if (isPristine) return textareaElement;

  return (
    <FormItem className={cn("w-full flex flex-col gap-1.5", wrapperClassName)}>
      {label && (
        <Label htmlFor={textareaId} className={disabled ? "opacity-50" : ""}>
          {label}
        </Label>
      )}
      <FormControl>{textareaElement}</FormControl>
      <FormMessage />
    </FormItem>
  );
}

export default Textarea;
